if (!customElements.get('pick-any-three-bundle')) {
  class PickAnyThreeBundle extends HTMLElement {
    connectedCallback() {
      if (this.pickerConnected) return;

      this.requiredQuantity = this.constructor.positiveInteger(this.dataset.requiredQuantity, 3);
      this.discountPercent = this.constructor.numberInRange(this.dataset.discountPercent, 0, 100, 10);
      this.allowBelowThreshold = this.dataset.allowBelowThreshold === 'true';
      this.unitSingular = this.dataset.unitSingular || 'bottle';
      this.unitPlural = this.dataset.unitPlural || `${this.unitSingular}s`;
      this.isB2B = this.dataset.b2b === 'true';
      this.componentCapacity = this.constructor.nonNegativeInteger(this.dataset.componentCapacity, 0);
      this.rows = Array.from(this.querySelectorAll('[data-bundle-choice]'));
      this.form = this.querySelector('[data-bundle-form]');
      this.progress = this.querySelector('[data-bundle-progress]');
      this.progressText = this.querySelector('[data-bundle-progress-text]');
      this.status = this.querySelector('[data-bundle-status]');
      this.selectionSummary = this.querySelector('[data-bundle-selection-summary]');
      this.error = this.querySelector('[data-bundle-error]');
      this.submitButton = this.querySelector('[data-bundle-submit]');
      this.subtotalOutput = this.querySelector('[data-bundle-subtotal]');
      this.savingsOutput = this.querySelector('[data-bundle-savings]');
      this.totalOutput = this.querySelector('[data-bundle-total]');
      this.currencyCode = this.dataset.currency || 'CAD';
      this.locale = this.dataset.locale || 'en-CA';
      this.cartDrawer = typeof document === 'undefined' ? null : document.querySelector('cart-drawer');

      this.boundClick = this.handleClick.bind(this);
      this.boundInput = this.handleInput.bind(this);
      this.boundSubmit = this.handleSubmit.bind(this);
      this.addEventListener('click', this.boundClick);
      this.addEventListener('input', this.boundInput);
      if (this.form) this.form.addEventListener('submit', this.boundSubmit);

      this.pickerConnected = true;
      this.sync();
    }

    handleClick(event) {
      const button = event.target.closest('[data-bundle-action]');
      if (!button || !this.contains(button) || this.busy) return;

      const row = button.closest('[data-bundle-choice]');
      const input = row?.querySelector('[data-bundle-quantity]');
      if (!row || !input || input.disabled) return;

      const current = this.constructor.nonNegativeInteger(input.value, 0);
      const delta = button.dataset.bundleAction === 'increment' ? 1 : -1;
      input.value = String(current + delta);
      this.sync(row);
    }

    handleInput(event) {
      if (!event.target.matches('[data-bundle-quantity]') || this.busy) return;
      this.sync(event.target.closest('[data-bundle-choice]'));
    }

    getChoices() {
      return this.rows.map((row) => {
        const input = row.querySelector('[data-bundle-quantity]');
        return {
          id: row.dataset.variantId,
          label: row.dataset.choiceLabel,
          quantity: input?.value ?? 0,
          max: row.dataset.max,
          available: row.dataset.available === 'true',
          priceCents: row.dataset.priceCents,
        };
      });
    }

    sync(changedRow = null) {
      if (changedRow) {
        const input = changedRow.querySelector('[data-bundle-quantity]');
        const max = this.constructor.quantityLimit(changedRow.dataset.max);
        const current = this.constructor.nonNegativeInteger(input?.value, 0);
        if (input) input.value = String(max === null ? current : Math.min(current, max));
      }

      const choices = this.getChoices();
      const summary = this.constructor.summarizeSelections(
        choices,
        this.requiredQuantity,
        this.allowBelowThreshold
      );
      const pricing = this.constructor.calculatePricing(
        summary.subtotalCents,
        this.discountPercent,
        this.isB2B,
        summary.thresholdMet
      );

      this.rows.forEach((row) => {
        const input = row.querySelector('[data-bundle-quantity]');
        const decrement = row.querySelector('[data-bundle-action="decrement"]');
        const increment = row.querySelector('.pick-any-three__stepper [data-bundle-action="increment"]');
        const addButton = row.querySelector('[data-bundle-add]');
        const stepper = row.querySelector('[data-bundle-stepper]');
        const quantity = this.constructor.nonNegativeInteger(input?.value, 0);
        const max = this.constructor.quantityLimit(row.dataset.max);
        const unavailable = row.dataset.available !== 'true' || max === 0;
        const selected = quantity > 0;

        row.dataset.selected = String(selected);
        if (addButton) {
          addButton.hidden = selected;
          addButton.disabled = this.busy || unavailable;
        }
        if (stepper) stepper.hidden = !selected;

        if (input) {
          input.disabled = this.busy || unavailable;
          input.setAttribute('aria-valuenow', String(quantity));
          if (max === null) input.removeAttribute?.('aria-valuemax');
          else input.setAttribute('aria-valuemax', String(max));
        }
        if (decrement) decrement.disabled = this.busy || unavailable || quantity === 0;
        if (increment) {
          increment.disabled = this.busy
            || unavailable
            || (max !== null && quantity >= max);
        }
      });

      if (this.progress) this.progress.value = Math.min(summary.totalQuantity, this.requiredQuantity);
      if (this.progressText) {
        const remaining = Math.max(0, this.requiredQuantity - summary.totalQuantity);
        if (this.allowBelowThreshold && this.isB2B) {
          this.progressText.textContent = summary.totalQuantity > 0
            ? `${summary.totalQuantity} ${this.unitForQuantity(summary.totalQuantity)} selected · Wholesale price`
            : `0 ${this.unitPlural} selected · Choose any quantity`;
        } else if (this.allowBelowThreshold) {
          this.progressText.textContent = summary.thresholdMet
            ? `${summary.totalQuantity} ${this.unitForQuantity(summary.totalQuantity)} selected · ${this.discountPercent}% discount unlocked`
            : `${summary.totalQuantity} ${this.unitForQuantity(summary.totalQuantity)} selected · Add ${remaining} more to save ${this.discountPercent}%`;
        } else {
          this.progressText.textContent = `${summary.totalQuantity} ${this.unitForQuantity(summary.totalQuantity)} selected · ${this.requiredQuantity} minimum`;
        }
      }
      if (this.selectionSummary) {
        const selectionText = this.constructor.formatSelectionSummary(choices);
        this.selectionSummary.textContent = selectionText;
        this.selectionSummary.hidden = !selectionText;
      }
      this.componentCapacity = this.constructor.availableCapacity(this.getChoices(), this.requiredQuantity);
      const purchaseCapacityRequired = this.allowBelowThreshold ? 1 : this.requiredQuantity;
      const inventoryInsufficient = this.componentCapacity < purchaseCapacityRequired;
      if (this.status && !this.busy && !this.activeError) {
        const remaining = Math.max(0, this.requiredQuantity - summary.totalQuantity);
        if (inventoryInsufficient) {
          this.status.textContent = this.allowBelowThreshold
            ? `No ${this.unitPlural} are available right now.`
            : `Not enough ${this.unitPlural} are available to build this bundle.`;
        } else if (this.allowBelowThreshold && this.isB2B) {
          this.status.textContent = summary.valid
            ? `Ready to add at your wholesale price.`
            : `Choose at least one ${this.unitSingular}.`;
        } else if (this.allowBelowThreshold && summary.thresholdMet) {
          this.status.textContent = `Discount unlocked. Add more ${this.unitPlural} or add to cart.`;
        } else if (this.allowBelowThreshold && summary.valid) {
          this.status.textContent = `Add ${remaining} more ${this.unitForQuantity(remaining)} to unlock ${this.discountPercent}% off.`;
        } else if (this.allowBelowThreshold) {
          this.status.textContent = `Choose at least one ${this.unitSingular}.`;
        } else {
          this.status.textContent = summary.valid
            ? `Minimum met. Add more ${this.unitPlural} or add to cart.`
            : summary.message;
        }
        this.status.dataset.state = summary.valid && !inventoryInsufficient ? 'ready' : 'selecting';
      }

      if (this.subtotalOutput) {
        this.subtotalOutput.textContent = this.formatMoney(pricing.subtotalCents);
      }
      if (this.savingsOutput) {
        this.savingsOutput.textContent = this.formatMoney(pricing.savingsCents);
      }
      if (this.totalOutput) {
        this.totalOutput.textContent = this.formatMoney(pricing.totalCents);
      }

      if (this.submitButton) {
        const submitDisabled = this.busy || !summary.valid || inventoryInsufficient;
        this.submitButton.disabled = submitDisabled;
        this.submitButton.setAttribute('aria-disabled', String(submitDisabled));
        const label = this.submitButton.querySelector('[data-bundle-submit-label]');
        if (label) {
          const remaining = Math.max(0, this.requiredQuantity - summary.totalQuantity);
          if (summary.valid) {
            label.textContent = `Add ${summary.totalQuantity} ${this.unitForQuantity(summary.totalQuantity)}`;
          } else if (this.allowBelowThreshold) {
            label.textContent = `Choose at least 1 ${this.unitSingular}`;
          } else {
            label.textContent = `Choose ${remaining} more ${this.unitForQuantity(remaining)}`;
          }
        }
      }

      return summary;
    }

    formatMoney(cents) {
      try {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currencyCode,
        }).format(cents / 100);
      } catch (error) {
        return `${this.currencyCode} ${(cents / 100).toFixed(2)}`;
      }
    }

    unitForQuantity(quantity) {
      const singular = this.unitSingular || 'bottle';
      const plural = this.unitPlural || `${singular}s`;
      return Number(quantity) === 1 ? singular : plural;
    }

    setError(message = '') {
      this.activeError = message;
      if (!this.error) return;
      this.error.hidden = !message;
      this.error.textContent = message;
    }

    setBusy(isBusy) {
      this.busy = isBusy;
      this.classList.toggle('is-loading', isBusy);
      this.sync();
    }

    async renderCart(data) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      const cartIconSection = data.sections?.['cart-icon-bubble'];
      if (cartIconBubble && typeof cartIconSection === 'string') {
        cartIconBubble.innerHTML = cartIconSection;
      }
      if (typeof this.cartDrawer?.renderContents === 'function') {
        await this.cartDrawer.renderContents(data, theme.settings.afterAtc === 'drawer');
      }
    }

    reset() {
      this.rows.forEach((row) => {
        const input = row.querySelector('[data-bundle-quantity]');
        if (input) input.value = '0';
      });
    }

    applySuccessfulAdd(items) {
      const quantitiesById = new Map(
        (Array.isArray(items) ? items : []).map(({ id, quantity }) => [
          this.constructor.positiveInteger(id, 0),
          this.constructor.nonNegativeInteger(quantity, 0),
        ])
      );

      this.rows.forEach((row) => {
        const max = this.constructor.quantityLimit(row.dataset.max);
        if (max === null) return;

        const variantId = this.constructor.positiveInteger(row.dataset.variantId, 0);
        const remaining = Math.max(0, max - (quantitiesById.get(variantId) || 0));
        row.dataset.max = String(remaining);
        row.dataset.available = String(row.dataset.available === 'true' && remaining > 0);

        const input = row.querySelector('[data-bundle-quantity]');
        if (input) {
          input.setAttribute('max', String(remaining));
          input.setAttribute('aria-valuemax', String(remaining));
        }
      });

      this.componentCapacity = this.constructor.availableCapacity(this.getChoices(), this.requiredQuantity);
    }

    async handleSubmit(event) {
      event.preventDefault();
      if (this.busy) return;

      const summary = this.sync();
      if (!summary.valid) {
        this.setError(summary.message);
        this.rows[0]?.querySelector('[data-bundle-quantity]')?.focus();
        return;
      }

      this.setError();
      this.setBusy(true);
      const sectionNames = ['cart-icon-bubble'];
      const drawerSection = this.cartDrawer?.closest('.shopify-section');
      if (drawerSection?.id) sectionNames.push(drawerSection.id.replace('shopify-section-', ''));

      try {
        const response = await fetch(theme.routes.cartAdd, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(this.constructor.buildCartPayload(
            summary.items,
            sectionNames,
            window.location.pathname
          )),
        });

        let data = {};
        try {
          data = await response.json();
        } catch (error) {
          if (response.ok) throw error;
        }

        if (!response.ok) {
          const cartError = new Error(this.constructor.responseError(data));
          cartError.isCartError = true;
          throw cartError;
        }

        this.applySuccessfulAdd(summary.items);
        await this.renderCart(data);
        this.reset();
        this.submitSucceeded = true;
        this.dispatchEvent(new CustomEvent('on:cart:add', {
          bubbles: true,
          detail: { variantIds: summary.items.map(({ id }) => id) },
        }));

        if (theme.settings.afterAtc === 'page') window.location.href = theme.routes.cart;
      } catch (error) {
        const message = error.isCartError
          ? error.message
          : 'We could not confirm the cart update. Review your cart before trying again.';
        this.setError(message);
        if (this.status) {
          this.status.textContent = message;
          this.status.dataset.state = 'error';
        }
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: { error: message },
        }));
        if (typeof this.cartDrawer?.refresh === 'function') await this.cartDrawer.refresh(true);
      } finally {
        this.setBusy(false);
        if (this.submitSucceeded && this.status) {
          this.status.textContent = `${summary.totalQuantity} ${this.unitForQuantity(summary.totalQuantity)} added to your cart.`;
          this.status.dataset.state = 'success';
          this.submitSucceeded = false;
        }
      }
    }

    static summarizeSelections(choices, requiredQuantity = 3, allowBelowThreshold = false) {
      const required = this.positiveInteger(requiredQuantity, 3);
      const itemsById = new Map();
      let totalQuantity = 0;
      let subtotalCents = 0;
      let issue = '';

      (Array.isArray(choices) ? choices : []).forEach((choice) => {
        const id = this.positiveInteger(choice.id, 0);
        const quantity = this.nonNegativeInteger(choice.quantity, -1);
        const max = this.quantityLimit(choice.max);
        const priceCents = this.nonNegativeInteger(choice.priceCents, 0);
        const label = typeof choice.label === 'string' && choice.label.trim()
          ? choice.label.trim()
          : 'This bottle';

        if (quantity < 0) {
          issue ||= 'Choose a whole-number quantity.';
          return;
        }
        totalQuantity += quantity;
        subtotalCents += priceCents * quantity;
        if (quantity === 0) return;
        if (!choice.available || max === 0) {
          issue ||= `${label} is unavailable.`;
          return;
        }
        if (max !== null && quantity > max) {
          issue ||= `${label} exceeds the available quantity.`;
          return;
        }
        if (!Number.isSafeInteger(id) || id <= 0) {
          issue ||= `${label} cannot be added right now.`;
          return;
        }
        itemsById.set(id, (itemsById.get(id) || 0) + quantity);
      });

      const thresholdMet = totalQuantity >= required;
      if (!issue && allowBelowThreshold && totalQuantity === 0) {
        issue = 'Choose at least one item.';
      } else if (!issue && !allowBelowThreshold && !thresholdMet) {
        const remaining = required - totalQuantity;
        issue = `Choose ${remaining} more bottle${remaining === 1 ? '' : 's'}.`;
      }

      return {
        items: Array.from(itemsById, ([id, quantity]) => ({ id, quantity })),
        totalQuantity,
        subtotalCents,
        thresholdMet,
        valid: !issue && (allowBelowThreshold ? totalQuantity > 0 : thresholdMet),
        message: issue,
      };
    }

    static calculatePricing(subtotalCents, discountPercent, isB2B, thresholdMet = true) {
      const subtotal = this.nonNegativeInteger(subtotalCents, 0);
      const discount = this.numberInRange(discountPercent, 0, 100, 0);
      const discountApplied = !isB2B && thresholdMet && discount > 0;
      const totalCents = discountApplied
        ? Math.round(subtotal * (100 - discount) / 100)
        : subtotal;

      return {
        subtotalCents: subtotal,
        savingsCents: subtotal - totalCents,
        totalCents,
        discountApplied,
      };
    }

    static formatSelectionSummary(choices) {
      const selections = (Array.isArray(choices) ? choices : [])
        .map((choice) => ({
          label: typeof choice.label === 'string' ? choice.label.trim() : '',
          quantity: this.nonNegativeInteger(choice.quantity, 0),
        }))
        .filter(({ label, quantity }) => label && quantity > 0)
        .map(({ label, quantity }) => `${quantity} × ${label}`);

      return selections.length ? `Your selection: ${selections.join(' · ')}` : '';
    }

    static availableCapacity(choices, requiredQuantity = 3) {
      const required = this.positiveInteger(requiredQuantity, 3);
      return (Array.isArray(choices) ? choices : []).reduce((total, choice) => {
        if (!choice.available) return total;
        const max = this.quantityLimit(choice.max);
        return total + (max === null ? required : max);
      }, 0);
    }

    static buildCartPayload(items, sections, sectionsUrl) {
      return {
        items,
        sections: sections.join(','),
        sections_url: sectionsUrl,
      };
    }

    static responseError(data) {
      if (typeof data?.description === 'string' && data.description.trim()) return data.description;
      if (typeof data?.message === 'string' && data.message.trim()) return data.message;
      if (data?.errors && typeof data.errors === 'object') {
        const messages = Object.values(data.errors).flat().filter(Boolean);
        if (messages.length) return messages.join(' ');
      }
      return 'These items could not be added. Check availability and try again.';
    }

    static positiveInteger(value, fallback) {
      const parsed = Number(value);
      return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : fallback;
    }

    static nonNegativeInteger(value, fallback) {
      const parsed = Number(value);
      return Number.isSafeInteger(parsed) && parsed >= 0 ? parsed : fallback;
    }

    static quantityLimit(value) {
      if (value === '' || value === null || typeof value === 'undefined') return null;
      return this.nonNegativeInteger(value, 0);
    }

    static numberInRange(value, min, max, fallback) {
      const parsed = Number(value);
      return Number.isFinite(parsed) && parsed >= min && parsed <= max ? parsed : fallback;
    }
  }

  customElements.define('pick-any-three-bundle', PickAnyThreeBundle);
}
